import React from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';

import IncomingPromotionsTable from './IncomingPromotionsTable';
import OutgoingPromotionsTable from './OutgoingPromotionsTable';
import type { IncomingPromotionRow, OutgoingPromotionRow } from './types';

/** Everything the two Promotions sub-tabs need, as one shape: `InnerTabs` is generic over a single props
 * type shared by every tab, so both panes take this and read their own slice (the delegated-authority
 * sub-tabs follow the same pattern). Threaded via the tabs' `props` channel rather than inline closures so
 * the route keeps a stable component type and the tables' sort/pagination state survives re-renders.
 * No loading flag: the tabs mount only once every fetch has resolved, and a reload never re-enters the
 * loading state, so a pane is never rendered while its list is loading. */
export interface PromotionsPaneProps {
  incoming: {
    rows: IncomingPromotionRow[];
    isError: boolean;
    onView: (requestUuid: string) => void;
    onRollback: (row: IncomingPromotionRow) => void;
  };
  outgoing: {
    rows: OutgoingPromotionRow[];
    isError: boolean;
    onView: (requestUuid: string) => void;
    /** "+ New promotion" is enabled only for a writer with a pairing this org is the source of. */
    canCreate: boolean;
    onCreate: () => void;
  };
}

/** Target-side: the promotions addressed to this org, with review / roll back actions. */
export const IncomingPromotionsPane: React.FC<PromotionsPaneProps> = ({ incoming }) => (
  <div className="flex flex-col gap-12 pt-16">
    <IncomingPromotionsTable
      rows={incoming.rows}
      isError={incoming.isError}
      onView={incoming.onView}
      onRollback={incoming.onRollback}
    />
  </div>
);

/** Source-side: the promotions this org sent out and the "+ New promotion" entry point. */
export const OutgoingPromotionsPane: React.FC<PromotionsPaneProps> = ({ outgoing }) => (
  <div className="flex flex-col gap-12 pt-16">
    <div className="flex items-center justify-end">
      <Button variant="contained" color="primary" disabled={!outgoing.canCreate} onClick={outgoing.onCreate}>
        + New promotion
      </Button>
    </div>
    <OutgoingPromotionsTable rows={outgoing.rows} isError={outgoing.isError} onView={outgoing.onView} />
  </div>
);
